import React from 'react'
import PropTypes from 'prop-types'
import { observer } from 'mobx-react'
import { withRouter } from 'react-router-dom'
import {
  enableHeaderMFESSR,
  enableHeaderMFE,
  i18nTranslate,
  enableCartMFE,
  IS_BROWSER,
  isB2BAccount,
  isB2B2C,
  application,
  isFeatureEnabled,
  getLocalePathParam,
  scrollState,
} from 'src/utils'
import {
  getSeparateCartAndCheckout,
  useStickyTop,
  getCookie,
} from 'config/appConfig'
import { customerLogout } from 'src/utils/signInUtils'
import { MetaDecorator } from 'src/views/components'
import { MicroFrontendLoader } from 'src/views/components/MicroFrontends/MicroFrontendLoader'
import { CartMFE } from 'src/views/components/CartMFE'
import {
  cartContainer,
  customerContainer,
  checkoutContainer,
  listsContainer,
} from 'src/models'
import { PUBSUB, subscribe, unSubscribeEvent } from 'src/pubsub'
import { isFetchAllPromoCodeEnabled } from 'src/deps/MyAccountMenuDeps'
import {
  getHeaderMfeParams,
  HEADER_MFE_APP_NAME,
  isHeaderQuickLinksEnabled,
} from 'src/utils/headerAuthService'
import { pageNames } from 'src/routes/pathParams'
import { shouldSuppressHeaderAndModals } from '../deps'
import {
  ACCOUNT_QUICK_LINKS_PANEL_TEST_ID,
  ACCOUNT_QUICK_LINKS_PANEL_WRAPPER_CLASS,
} from '../constants'

function hasPathBoundary(path, prefix) {
  return path === prefix || path.startsWith(`${prefix}/`)
}

function stripTrailingSlashes(path) {
  let end = path.length
  while (end > 0 && path.charAt(end - 1) === '/') {
    end -= 1
  }
  return path.slice(0, end)
}

function getActiveBasename(historyPathname) {
  if (
    !IS_BROWSER ||
    typeof window?.location?.pathname !== 'string' ||
    typeof historyPathname !== 'string'
  ) {
    return ''
  }

  const browserPathname = window.location.pathname

  if (!historyPathname || historyPathname === '/') {
    // Drop trailing slash(es) so the basename has no dangling separator.
    return stripTrailingSlashes(browserPathname)
  }

  if (!browserPathname.endsWith(historyPathname)) {
    return ''
  }

  return browserPathname.slice(
    0,
    browserPathname.length - historyPathname.length
  )
}

function normalizeInternalRoutePath(path, locale, historyPathname) {
  const localePrefix = locale ? `/${locale}` : ''
  const activeBasename = getActiveBasename(historyPathname)
  const candidatePrefixes = [activeBasename, localePrefix]
    .filter(Boolean)
    .sort((left, right) => right.length - left.length)

  const matchingPrefix = candidatePrefixes?.find(prefix =>
    hasPathBoundary(path, prefix)
  )

  if (!matchingPrefix) {
    return path
  }

  return path.slice(matchingPrefix.length) || '/'
}

function hasQuickLinksProfileData(profileResponse) {
  return Boolean(profileResponse) && Object.keys(profileResponse).length > 0
}

/**
 * Gates account-profile QuickLinks beside Header MFE.
 * Same store flag as legacy UserMenu: when false, header-mfe shows inline
 * Hi {name} / Sign Out and this embed is not mounted.
 *
 * @see UserMenu.registerUserOktaComponent
 */
function shouldRenderQuickLinks({
  isBrowser,
  isHeaderMfeEnabled,
  enableQuickLinks,
  isRegisterUser,
  profileResponse,
}) {
  return (
    isBrowser &&
    isHeaderMfeEnabled &&
    enableQuickLinks &&
    isRegisterUser === true &&
    hasQuickLinksProfileData(profileResponse)
  )
}

@observer
class HeaderMFE extends React.Component {
  headerContainerRef = React.createRef()
  heightObserver = null
  observerAttempted = false
  hasMountedHeaderLoader = false

  observeHeaderHeight = () => {
    const container = this.headerContainerRef?.current
    if (!IS_BROWSER || !container) {
      return
    }

    if (!window.ResizeObserver) {
      this.observerAttempted = true
      return
    }

    const appRoot =
      useStickyTop ? document.getElementById('app-root') : null

    const measureContainerHeight = () =>
      container.getBoundingClientRect().height

    const applyHeight = height => {
      scrollState.headerHeight = height
      if (appRoot) {
        appRoot.style.paddingTop = `${height}px`
      }
    }

    this.heightObserver = new ResizeObserver(() => {
      applyHeight(measureContainerHeight())
    })
    this.heightObserver.observe(container)
    applyHeight(measureContainerHeight())
  }

  componentDidMount() {
    const enableMiniCart =
      application?.isTabletOrLarger &&
      (!isB2BAccount() || isB2B2C()) &&
      enableCartMFE() &&
      IS_BROWSER

    if (enableMiniCart) {
      this.addToCartToken = subscribe(
        PUBSUB.ADD_TO_CART,
        this.handleAddToCartFromHomeMfe
      )
    }

    this.internalRoutingToken = subscribe(
      PUBSUB.INTERNAL_ROUTING,
      this.handleInternalRouting
    )

    this.navigateToViewCartToken = subscribe(
      PUBSUB.NAVIGATE_TO_VIEW_CART,
      this.handleNavigateToViewCart
    )

    this.signOutToken = subscribe(PUBSUB.SIGN_OUT, this.handleSignOutEvent)

    this.initializeListAPI()

    if (IS_BROWSER) {
      this.observeHeaderHeight()
      this.observeCartRowBottom()
    }
  }

  /**
   * Bootstraps favorites/save-for-later list ids so the favorite icon's
   * add/remove calls have a listId to target. The legacy header did this in
   * UserMenu's componentDidMount; HeaderMFE never called it, so the
   * favoritesListId stayed empty and listservice/lists was never hit.
   */
  initializeListAPI = () => {
    const customerResponse =
      customerContainer?.profileResponse?.customer ||
      customerContainer?.profileResponse

    if (customerResponse?.id) {
      if (getCookie('x-role-user')) {
        listsContainer.initFavoritesAndSaveForLater(true)
      }
    } else {
      this.listApiTimeoutId = setTimeout(() => {
        this.initializeListAPI()
      }, 300)
    }
  }

  componentDidUpdate() {
    if (
      !this.heightObserver &&
      !this.observerAttempted &&
      this.headerContainerRef?.current
    ) {
      this.observerAttempted = true
      this.observeHeaderHeight()
    }
  }

  componentWillUnmount() {
    clearTimeout(this.listApiTimeoutId)
    if (this.heightObserver) {
      this.heightObserver.disconnect()
      this.heightObserver = null
    }
    if (this.addToCartToken) {
      unSubscribeEvent(this.addToCartToken)
    }
    if (this.internalRoutingToken) {
      unSubscribeEvent(this.internalRoutingToken)
    }
    if (this.navigateToViewCartToken) {
      unSubscribeEvent(this.navigateToViewCartToken)
    }
    if (this.signOutToken) {
      unSubscribeEvent(this.signOutToken)
    }
    this.headerResizeObserver?.disconnect()
    this.headerMutationObserver?.disconnect()
    document.documentElement.style.removeProperty('--header-mfe-cart-row-bottom')
  }

  observeCartRowBottom = () => {
    const container = this.headerContainerRef?.current
    if (!container || typeof ResizeObserver === 'undefined') return

    const setVar = () => {
      const cartBtn = container.querySelector('[data-testid="cart"]')
      if (!cartBtn) return
      const top =
        cartBtn.getBoundingClientRect().bottom -
        container.getBoundingClientRect().top
      if (top > 0) {
        document.documentElement.style.setProperty(
          '--header-mfe-cart-row-bottom',
          `${Math.round(top)}px`
        )
      }
    }

    this.headerResizeObserver = new ResizeObserver(setVar)
    this.headerResizeObserver.observe(container)

    this.headerMutationObserver = new MutationObserver(() => {
      if (container.querySelector('[data-testid="cart"]')) {
        setVar()
        this.headerMutationObserver.disconnect()
      }
    })
    this.headerMutationObserver.observe(container, {
      childList: true,
      subtree: true,
    })
  }

  handleInternalRouting = (eventName, options) => {
    const { path } = options || {}
    if (path && this.props.history) {
      const locale = getLocalePathParam()
      // Account-profile quick links can publish locale-prefixed absolute paths;
      // normalize them back to the host router contract before pushing.
      const normalizedPath = normalizeInternalRoutePath(
        path,
        locale,
        this.props.history?.location?.pathname
      )

      this.props.history.push(normalizedPath || '/')
    }
  }

  handleNavigateToViewCart = async (eventName, data) => {
    const { fromCheckout, fromMobile } = data || {}

    if (fromMobile) {
      if (this.props.history) {
        this.props.history.push(pageNames.viewCart)
      }
      return
    }

    if (fromCheckout && getSeparateCartAndCheckout()) {
      if (cartContainer?.cartResponse?.loyaltyDetails) {
        try {
          await checkoutContainer.deleteDiscountReward()
        } catch (error) {
          console.error('Failed to delete discount reward:', error)
        }
      }

      if (this.props.history) {
        this.props.history.push(pageNames.viewCart)
      }
      return
    }

    if (fromCheckout && !getSeparateCartAndCheckout()) {
      return
    }

    if (!fromMobile && !fromCheckout) {
      console.warn(
        'NAVIGATE_TO_VIEW_CART event received without fromMobile or fromCheckout flag',
        { eventName, data }
      )
    }
  }

  handleAddToCartFromHomeMfe = async () => {
    await cartContainer.viewCart()
    cartContainer.showAddToCartSuccessMessage()
    cartContainer.toggleCartVisibility()
  }

  // account-profile QuickLinks publishes SignOut via pubsub. HeaderMFE owns that
  // subscription when enableHeaderMFE() is true. When false, legacy UserMenu
  // subscribes to the same event (UserMenu.subscribeToEvent → SIGN_OUT).
  handleSignOutEvent = () => {
    customerLogout().catch(error => {
      console.error('[HeaderMFE] sign-out failed', error)
      // Fail-safe: logout state may be left dirty (partial clear, no redirect).
      // Send the user to sign-in rather than leaving them on a stale session.
      this.props.history?.push(pageNames.signIn)
    })
  }

  /**
   * @param {{ enableQuickLinks?: boolean, isCustomerRegistered?: boolean, enableLiveEvent?: boolean }} [headerParams]
   */
  renderAccountProfileQuickLinks = headerParams => {
    const profileResponse = customerContainer?.profileResponse

    if (
      !shouldRenderQuickLinks({
        isBrowser: IS_BROWSER,
        isHeaderMfeEnabled: enableHeaderMFE(),
        enableQuickLinks:
          headerParams?.enableQuickLinks ?? isHeaderQuickLinksEnabled(),
        isRegisterUser: headerParams?.isCustomerRegistered,
        profileResponse,
      })
    ) {
      return null
    }

    return (
      <div
        data-testid={ACCOUNT_QUICK_LINKS_PANEL_TEST_ID}
        className={ACCOUNT_QUICK_LINKS_PANEL_WRAPPER_CLASS}
        // Avoid an extra flex box; children participate in the header layout directly.
        // display:contents removes this node from the a11y tree — OK while the
        // wrapper has no role; smoke-test VoiceOver on iOS Safari if adding aria-*.
        style={{ display: 'contents' }}>
        <MicroFrontendLoader
          loaderId="mfe-loader-shell-header-account-profile-QuickLinks"
          remoteConfig={{ appName: 'account-profile', component: 'QuickLinks' }}
          params={{
            routerHistory: this.props.history,
            customerData: profileResponse,
            pubsubControlled: true,
            enableStoreCreditFlooredValue: isFeatureEnabled(
              'enableStoreCreditFlooredValue'
            ),
            isFetchAllPromoCodeEnabled: isFetchAllPromoCodeEnabled(),
            enableLiveEvent: headerParams?.enableLiveEvent === true,
          }}
        />
      </div>
    )
  }

  render() {
    if (shouldSuppressHeaderAndModals(this.props.location)) {
      return null
    }
    const headerTitle = i18nTranslate('header.title', 'Nu Skin | Header')
    const remoteConfig = {
      appName: HEADER_MFE_APP_NAME,
      component: 'App',
    }

    const params = getHeaderMfeParams(this.props.history)
    // CSR: mount only after the first getProfile() completes so the remote loads
    // with final customerData / isCustomerRegistered (RemoteLoader init runs once
    // on mount). Latch this with an instance flag instead of re-reading
    // isProfileCallOver on every render — any later getProfile() call (route
    // change, session boot, etc.) flips isProfileCallOver false->true again, and
    // re-gating on it here would unmount/remount the whole header (CX130-2343).
    if (
      !this.hasMountedHeaderLoader &&
      (!IS_BROWSER || customerContainer?.isProfileCallOver)
    ) {
      this.hasMountedHeaderLoader = true
    }
    const canMountHeaderLoader = this.hasMountedHeaderLoader

    return (
      <>
        <MetaDecorator
          title={headerTitle}
          imageUrl=""
          imageAlt=""
          description={headerTitle}
          location={this.props}
        />
        <div
          className="header-mfe-container"
          data-testid="qa-header-mfe"
          ref={this.headerContainerRef}>
          {canMountHeaderLoader ? (
            <MicroFrontendLoader
              loaderId="mfe-loader-shell-header_mfe-App"
              isToEnableSSR={enableHeaderMFESSR()}
              remoteConfig={remoteConfig}
              params={params}
            />
          ) : null}
          <div className="header-mfe-mini-cart-anchor">
            <CartMFE
              routerHistory={this.props?.history}
              customerData={customerContainer?.profileResponse}
            />
          </div>
        </div>
        {canMountHeaderLoader
          ? this.renderAccountProfileQuickLinks(params)
          : null}
      </>
    )
  }
}
HeaderMFE.propTypes = {
  history: PropTypes.object,
  location: PropTypes.object,
}

export default withRouter(HeaderMFE)
export { HeaderMFE, normalizeInternalRoutePath, shouldRenderQuickLinks }
